/* ===========================================================================
 *  Wat de Worker en het scherm allebei kennen
 *
 *  Eén bestand, door beide tsconfigs ingesloten. Verandert er hier iets, dan
 *  zegt de typecontrole aan BEIDE kanten wat er niet meer past.
 * =========================================================================== */

/**
 * De vier standen, en wat ze beloven.
 *
 *   werkt     gemeten, en goed
 *   traag     gemeten, het werkt, maar langzaam of half (een wekker die
 *             gisteren een keer misging, een functie die achterloopt)
 *   storing   gemeten, en het werkt niet
 *   onbekend  NIET gemeten, of niet te meten. Nooit groen als we het niet
 *             weten: dat is de belofte van de statuspagina (meet.mjs), en
 *             die geldt hier net zo.
 */
export type Staat = 'werkt' | 'traag' | 'storing' | 'onbekend'

export type Groep =
  | 'web' | 'supabase' | 'functies' | 'database' | 'wekkers'
  | 'koppelingen' | 'leveranciers' | 'apparaten' | 'kantoor' | 'bouw' | 'health'

export const GROEPEN: { key: Groep; naam: string; uitleg: string }[] = [
  { key: 'web', naam: 'Websites en subdomeinen', uitleg: 'Elk adres dat we zelf of via een ander laten draaien, van buitenaf gemeten.' },
  { key: 'supabase', naam: 'Supabase', uitleg: 'Inloggen, de database via de API, opslag, en het luik van Health zelf.' },
  { key: 'functies', naam: 'Serverfuncties', uitleg: 'Elke edge function: draait hij, staat de poort goed, welke versie.' },
  { key: 'database', naam: 'Database', uitleg: 'Verbindingen, trage queries, sloten, migraties.' },
  { key: 'wekkers', naam: 'Wekkers', uitleg: 'De geplande taken in de database (pg_cron) en wat de gewekte functie terugzei.' },
  { key: 'koppelingen', naam: "Koppelingen en API's", uitleg: 'Exact, de post, MongoDB, de eigen AI, de inlogkoppelingen.' },
  { key: 'leveranciers', naam: 'Leveranciers', uitleg: 'Wat de statuspagina van GitHub, Cloudflare, Supabase en de anderen zelf zegt.' },
  { key: 'apparaten', naam: 'Apparaten', uitleg: "De pc's die zich melden." },
  { key: 'kantoor', naam: 'Kantoor-pc', uitleg: 'Wat de melder op de pc zelf meet: LM Studio, de lezer, de videokaart, schijven, de klok.' },
  { key: 'bouw', naam: 'Bouwstraten', uitleg: 'De laatste run van elke workflow op GitHub.' },
  { key: 'health', naam: 'Health zelf', uitleg: 'Meet de meter nog? En bereikt hij zijn eigen database?' },
]

/** Hoe erg het is als dit rood wordt. 'laag' geeft nooit een seintje. */
export type Ernst = 'hoog' | 'normaal' | 'laag'

/** Eén uitslag in een ronde. Kort, want er staan er zeventig per minuut. */
export interface Uitslag {
  s: Staat
  ms?: number | null
  /** Uitleg in gewone taal: wat er mis is, of wat er gezien is. */
  u?: string | null
  /** Details voor wie doorklikt: codes, kopregels, aantallen. */
  d?: Record<string, unknown> | null
  /** Wanneer deze controle zelf het laatst echt mat. */
  at: number
  /** Sinds wanneer hij in deze stand staat. */
  sinds: number
}

/** Wat er van een controle vaststaat, los van een ronde. */
export interface ControleInfo {
  id: string
  groep: Groep
  naam: string
  /** Wat er gemeten wordt, in één zin. */
  wat: string
  ernst: Ernst
  /** Om de hoeveel minuten. */
  elke: number
  /** Een adres om heen te gaan, als er een is. */
  link?: string | null
}

export interface Meting extends ControleInfo, Uitslag {}

export interface Telling { werkt: number; traag: number; storing: number; onbekend: number }

export interface Overzicht {
  /** Wanneer de laatste ronde begon. */
  at: number | null
  /** Hoe lang die duurde. */
  duurMs: number | null
  metingen: Meting[]
  telling: Telling
  storingen: Storing[]
  /** Hoe oud de laatste ronde mag zijn voor het scherm hem "stil" noemt. */
  stilNa: number
  tokens: TokenStand
}

export interface TokenStand {
  github: boolean
  cloudflare: boolean
  supabase: boolean
  melder: boolean
}

export interface Storing {
  id: string
  controle: string
  naam: string
  groep: Groep
  begon: number
  eind: number | null
  uitleg: string | null
  laatste: string | null
  gemeldAt: number | null
  herstelGemeldAt: number | null
  erkendDoor: string | null
  erkendAt: number | null
  notitie: string | null
}

export interface UurPunt {
  uur: number
  aantal: number
  werkt: number
  traag: number
  storing: number
  onbekend: number
  msGem: number | null
  msMax: number | null
}

export interface MinuutPunt { at: number; s: Staat; ms: number | null; u: string | null }

export interface Detail {
  meting: Meting | null
  minuten: MinuutPunt[]
  uren: UurPunt[]
  storingen: Storing[]
  beschikbaar: { dag: number | null; week: number | null; maand: number | null }
}

/* ------------------------------------------------------------------ *
 *  Het logboek
 * ------------------------------------------------------------------ */

export type Niveau = 'fout' | 'waarschuwing' | 'info'

export interface Logregel {
  at: number
  bron: string
  niveau: Niveau
  onderwerp: string | null
  tekst: string
  details: Record<string, unknown> | null
}

export interface Logbron {
  key: string
  naam: string
  /** Waar het vandaan komt; voor het scherm. */
  waar: string
  /** Staat het aan? Een bron zonder token zegt waarom niet. */
  beschikbaar: boolean
  waarom?: string
}

/* ------------------------------------------------------------------ *
 *  Melders: de pc's die zich melden
 * ------------------------------------------------------------------ */

export interface MelderControle {
  id: string
  naam: string
  wat: string
  s: Staat
  ms?: number | null
  u?: string | null
  d?: Record<string, unknown> | null
}

export interface MelderBericht {
  naam: string
  versie: string
  /** Wat de melder zelf heeft gemeten. */
  controles: MelderControle[]
  /** Nieuwe regels uit de logbestanden die hij volgt. */
  regels: { at: number; bron: string; niveau: Niveau; tekst: string }[]
  info: Record<string, unknown>
}

export interface Melder {
  naam: string
  gezienAt: number
  eerstAt: number
  versie: string | null
  adres: string | null
  info: Record<string, unknown> | null
}

/* ------------------------------------------------------------------ *
 *  Wie ben ik
 * ------------------------------------------------------------------ */

export interface Ik {
  id: string
  naam: string
  email: string
  rollen: string[]
  vestiging: string | null
}
